import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useStore } from '@bluheadless/ui-logic/src/providers/store'
import { removeTrailingSlashes } from '@bluheadless/ui-logic/src/utils/url'
import { cx } from '@emotion/css'
import { Grid } from '@mui/material'
import { useRouter } from 'next/router'
import { shape, string, bool, oneOfType, object } from 'prop-types'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
	EmailShareButton,
	FacebookShareButton,
	PinterestShareButton,
	TwitterShareButton,
	WhatsappShareButton,
} from 'react-share'
import MailIcon from '../../atoms/icons/mail-icon'
import ShareIcon from '../../atoms/icons/share-icon'
import FacebookIcon from '../../atoms/icons/social/facebook-icon'
import PinterestIcon from '../../atoms/icons/social/pinterest-icon'
import TwitterIcon from '../../atoms/icons/social/twitter-icon'
import WhatsappIcon from '../../atoms/icons/social/whatsapp-icon'
import Typography from '../../atoms/typography'
import Button from '../../molecules/button'
import Dialog from '../../molecules/dialog'
import { ButtonStyled, DialogStyled, Root } from './social-share.styled'
import { resolveHref } from 'next/dist/shared/lib/router/utils/resolve-href'

const SocialShare = ({ className, title, url, media, buttonProps, dialogProps, enabled: _enabled }) => {
	const [toggleDialog, setToggleDialog] = useState(false)
	const { formatMessage } = useIntl()
	const { uri: storeUri } = useStore()
	const router = useRouter()

	const {
		socialShare: {
			enabled: enabledSocialShare,
			socials: {
				facebook: facebookEnabled,
				twitter: twitterEnabled,
				pinterest: pinterestEnabled,
				whatsapp: whatsappEnabled,
				email: emailEnabled,
			},
		},
	} = useConfig()

	const onClickHandler = async (data) => {
		if (!navigator?.canShare) return setToggleDialog(true)

		try {
			navigator.canShare(data) ? await navigator.share(data) : setToggleDialog(true)
		} catch (e) {
			console.error(e)
		}
		return
	}

	const shareUrl = useMemo(
		() =>
			url
				? typeof url === 'object'
					? removeTrailingSlashes(storeUri) + resolveHref(router, url)
					: url
				: storeUri
				? removeTrailingSlashes(storeUri) + router.asPath
				: null,
		[router, storeUri, url]
	)

	const enabled = _enabled ?? enabledSocialShare

	return (
		enabled && (
			<Root className={cx('SocialShare-root', className)}>
				<ButtonStyled
					variant="icon"
					Icon={ShareIcon}
					iconProps={{ color: 'primary', fontSize: 'button' }}
					onClick={() => onClickHandler({ text: title, url: shareUrl })}
					sx={{ padding: 0 }}
					{...buttonProps}
				/>
				<DialogStyled open={toggleDialog} showButtonClose onClose={() => setToggleDialog(false)} {...dialogProps}>
					<Grid>
						<Typography variant="h1" component="h2" mb={4} textAlign="center">
							{formatMessage({ id: 'share_on_social_title' })}
						</Typography>
					</Grid>
					<Grid alignItems="center" justifyContent="center" gap={2} display="flex" mb={3}>
						{facebookEnabled && (
							<FacebookShareButton quote={title} url={shareUrl}>
								<FacebookIcon fontSize="large" />
							</FacebookShareButton>
						)}
						{twitterEnabled && (
							<TwitterShareButton title={title} url={shareUrl}>
								<TwitterIcon fontSize="large" />
							</TwitterShareButton>
						)}

						{pinterestEnabled && media && (
							<PinterestShareButton description={title} url={shareUrl} media={media}>
								<PinterestIcon fontSize="large" />
							</PinterestShareButton>
						)}
						{whatsappEnabled && (
							<WhatsappShareButton title={title} url={shareUrl}>
								<WhatsappIcon fontSize="large" />
							</WhatsappShareButton>
						)}
						{emailEnabled && (
							<EmailShareButton subject={title} body="" url={shareUrl}>
								<MailIcon fontSize="large" />
							</EmailShareButton>
						)}
					</Grid>
				</DialogStyled>
			</Root>
		)
	)
}

SocialShare.defaultProps = {
	title: '',
	url: null,
	media: null,
}

SocialShare.propTypes = {
	className: string,
	title: string.isRequired,
	url: oneOfType([string, object]),
	media: string,
	buttonProps: shape({
		...Button.propTypes,
	}),
	dialogProps: shape({
		...Dialog.propTypes,
	}),
	enabled: bool,
}

export default SocialShare
